import { PromoVideoFrame } from '@/components/promo-video-frame'
import { Button } from '@/components/ui/button'
import { getArenaUrl } from '@/lib/arena'
import { useI18n } from '@/lib/i18n'
import { withLanguagePath } from '@/lib/url-language-utils'
import { ArrowRight, Check } from 'lucide-react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'

const heroColumnsClassName = 'lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-10 lg:px-8'

export function HomePromoVideoSection() {
  const { language } = useI18n()

  return (
    <section
      id="video"
      className="bg-section-dark px-4 py-20 sm:px-6 lg:pointer-events-none lg:relative lg:z-10 lg:col-start-1 lg:row-start-1 lg:self-center lg:bg-transparent lg:px-0 lg:py-20"
    >
      <div className={`container px-0 max-lg:max-w-3xl lg:grid lg:items-center ${heroColumnsClassName}`}>
        <PromoVideoFrame
          language={language}
          testId="home-promo-video"
          className="border border-slate-200 bg-slate-950 shadow-xl shadow-slate-900/10 dark:border-white/10 dark:shadow-black/40 lg:pointer-events-auto lg:col-start-2"
        />
      </div>
    </section>
  )
}

export function HeroSection() {
  const { language, t } = useI18n()
  const arenaOrigin = getArenaUrl()
  const gameUrl = arenaOrigin
    ? `${arenaOrigin}${withLanguagePath('/new', language)}`
    : withLanguagePath('/new', language)

  const titleParts = t('hero.title')
    .split('\n')
    .map(part => part.trim())
    .filter(Boolean)

  const optionalT = (key: string) => {
    const value = t(key)
    return value === key ? undefined : value
  }

  const handleButtonClick = (link: string) => {
    const element = document.querySelector(link)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  const subtitleText = t('hero.eyebrow')
  const commaIndex = subtitleText.search(/[，,،、]/)
  const hasComma = commaIndex !== -1
  const firstLine = hasComma ? subtitleText.substring(0, commaIndex + 1) : subtitleText
  const secondLine = hasComma ? subtitleText.substring(commaIndex + 1) : ''

  const renderSubtitle = () => (
    <div className="mb-6 flex w-full items-center justify-center gap-2 px-4 sm:gap-4">
      { }
      <div className="h-px max-w-[40px] flex-1 bg-gradient-to-r from-transparent to-amber-500/80 dark:to-amber-400/80 sm:max-w-[96px]" />

      { }
      <div className="flex flex-col items-center">
        {hasComma ? (
          <>
            <span className="whitespace-nowrap text-center text-sm font-medium text-slate-600 dark:text-slate-300 sm:text-base">
              {firstLine}
            </span>
            <span className="whitespace-nowrap text-center text-sm font-medium text-slate-600 dark:text-slate-300 sm:text-base">
              {secondLine.trim()}
            </span>
          </>
        ) : (
          <p className="whitespace-nowrap text-center text-sm font-medium text-slate-600 dark:text-slate-300 sm:text-base md:text-lg">
            {subtitleText}
          </p>
        )}
      </div>

      { }
      <div className="h-px max-w-[40px] flex-1 bg-gradient-to-l from-transparent to-amber-500/80 dark:to-amber-400/80 sm:max-w-[96px]" />
    </div>
  )

  return (
    <section className="relative flex min-h-dvh items-center overflow-hidden bg-gradient-to-br from-section-light to-section-dark py-16 sm:py-24 lg:py-20">
      <div className={`container mx-auto grid grid-cols-1 items-center gap-10 px-4 ${heroColumnsClassName}`}>
        { }
        <div className="text-center lg:text-start">

          { }
          {renderSubtitle()}

          { }
          <h1 className="mx-auto mb-4 mt-12 max-w-[80vw] break-words text-[min(6vw,1.85rem)] font-bold leading-tight tracking-tight text-slate-800 dark:text-slate-100 sm:mt-0 sm:max-w-none sm:whitespace-nowrap sm:text-[2rem] md:text-[2.5rem] lg:mb-6 lg:whitespace-normal lg:text-[2.5rem] lg:[text-wrap:balance] xl:text-[3rem]">
            {titleParts.length > 1
              ? titleParts.map((part, idx) => (
                  <Fragment key={idx}>
                    <span className="block sm:inline lg:inline-block">{part}</span>
                    {idx < titleParts.length - 1 ? (
                      <span className="hidden sm:inline"> </span>
                    ) : null}
                  </Fragment>
                ))
              : t('hero.title')}
          </h1>

          { }
          {optionalT('hero.bullet1') ? (
            <ul className="mx-auto mb-8 w-full max-w-[80vw] space-y-3 px-2 text-start sm:mx-0 sm:max-w-none sm:px-0">
              {(
                [
                  'hero.bullet1',
                  'hero.bullet2',
                  'hero.bullet3',
                ] as const
              )
                .map(key => optionalT(key))
                .filter(Boolean)
                .map((text, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-base leading-relaxed text-slate-700 dark:text-slate-200 md:text-lg"
                  >
                    <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300">
                      <Check className="size-4" />
                    </span>
                    <span className="text-pretty" dangerouslySetInnerHTML={{ __html: text as string }} />
                  </li>
                ))}
            </ul>
          ) : (
            <p className="mb-8 w-full text-center text-lg leading-relaxed text-slate-600 dark:text-slate-300 md:text-xl lg:text-start">
              {t('hero.descriptionFull')}
            </p>
          )}

          { }
          <div className="flex flex-col items-center gap-6 sm:items-start lg:items-start">
            { }
            <Button
              asChild
              size="lg"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-2xl border-2 border-slate-400/25 bg-gradient-to-br from-slate-800 via-slate-900 to-black p-6 text-sm font-medium text-slate-100 shadow-lg shadow-black/60 backdrop-blur-md transition-all before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:to-transparent before:opacity-50 before:transition-opacity after:absolute after:inset-0 after:bg-gradient-to-tl after:via-transparent after:to-white/10 after:opacity-40 after:transition-opacity hover:border-slate-300/40 hover:from-slate-700 hover:via-slate-800 hover:to-slate-900 hover:shadow-xl hover:shadow-slate-800/70 hover:before:opacity-70 hover:after:opacity-65 dark:border-white/40 dark:from-white/25 dark:via-zinc-100/30 dark:to-white/35 dark:text-slate-900 dark:shadow-white/20 dark:backdrop-blur-xl dark:before:from-white/50 dark:before:via-white/30 dark:before:opacity-70 dark:after:from-white/40 dark:after:to-white/30 dark:after:opacity-60 dark:hover:border-white/60 dark:hover:from-white/35 dark:hover:via-zinc-50/40 dark:hover:to-white/45 dark:hover:shadow-white/30 dark:hover:before:opacity-90 dark:hover:after:opacity-85 sm:px-8 sm:py-7 sm:text-base"
            >
              <Link to="/contact-us">
                <span className="relative z-10 inline-flex items-center gap-2">
                  {t('hero.analysisReportButton')}
                  <ArrowRight className="size-4 sm:size-5" />
                </span>
              </Link>
            </Button>

            { }
            <div className="flex flex-row flex-wrap items-center justify-center gap-4 sm:items-start sm:justify-start">
              <Button
                asChild
                size="lg"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl border-2 border-amber-400/35 bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 px-6 py-5 text-sm font-medium text-amber-950 shadow-lg shadow-amber-500/55 backdrop-blur-md transition-all before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/45 before:via-amber-300/25 before:to-transparent before:opacity-65 before:transition-opacity after:absolute after:inset-0 after:bg-gradient-to-tl after:from-yellow-300/30 after:via-transparent after:to-white/25 after:opacity-55 after:transition-opacity hover:border-amber-500/55 hover:from-yellow-400 hover:via-amber-500 hover:to-yellow-600 hover:shadow-xl hover:shadow-amber-600/65 hover:before:opacity-85 hover:after:opacity-80 dark:border-amber-500/35 dark:from-amber-400 dark:via-yellow-500 dark:to-amber-600 dark:text-black dark:shadow-amber-500/55 dark:hover:border-amber-400/60 dark:hover:from-yellow-400 dark:hover:via-amber-500 dark:hover:to-yellow-600 dark:hover:shadow-amber-500/70 sm:px-8 sm:py-6"
              >
                <a href={gameUrl}>
                  <span className="relative z-10">{t('hero.playButton')}</span>
                </a>
              </Button>

              <Button
                asChild
                size="lg"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-border bg-background px-6 py-5 text-sm font-medium text-foreground shadow-md transition-all hover:border-primary/30 hover:bg-accent hover:text-accent-foreground hover:shadow-lg sm:px-8 sm:py-6"
              >
                <a
                  href="#benchmark"
                  onClick={(event) => {
                    event.preventDefault()
                    handleButtonClick('#benchmark')
                  }}
                >
                  {t('hero.labelDataButton')}
                  <ArrowRight className="size-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  )
}
